import { FEHLERTEXTE, fehlertext } from "./fehlertext.ts";

/**
 * Ursachesaetze des Uebersetzers (`FEHLERTEXTE`), nach denen UNGEWISS ist, ob der Server die Schreibaktion trotzdem
 * ausgefuehrt hat: die Antwort ging unterwegs verloren (Netz, Zeitlimit, Proxy-502/504, 500). Bei Aktionen ohne
 * Idempotenzschutz - die Anlage einer Bestellung in JTL (R-T6b), die Teillieferung (R-T7d) - waere hier ein
 * Wiederholungsrat der Weg in die Doppelanlage; die Meldung sagt stattdessen, dass der Ausgang unklar ist.
 * Dazu gehoert die abgerissene Antwort eines Schreibens (2xx, Koerper weg): der Server hat bearbeitet, das Ergebnis
 * fehlt. Der Lesetext (`antwortUnvollstaendigLesen`) gehoert NICHT hierher - ein Lesen aendert nichts.
 */
export const URSACHEN_MIT_UNKLAREM_AUSGANG: readonly string[] = [
  FEHLERTEXTE.verbindung,
  FEHLERTEXTE.zeitueberschreitung,
  FEHLERTEXTE.serverAntwortetNicht,
  FEHLERTEXTE.serverfehler,
  FEHLERTEXTE.antwortUnvollstaendigSchreiben,
];

/**
 * Ursachesaetze des Uebersetzers (`FEHLERTEXTE`), die zum Wiederholen raten oder nur "irgendetwas ging schief" sagen.
 * Hinter den Meldungen von R4 (die Aktion ist GELUNGEN, nur das Nachladen scheiterte: `meldeNachladeFehler`,
 * `nachladeFehlertext`) und der Anlage steht keiner dieser Saetze: der Nutzer wiederholte sonst eine Aktion, die schon
 * gelungen ist - die Doppelanlage vom 2026-08-15 hat drei Bestellungen in JTL erzeugt (R-T6a), ein zweiter Upload
 * eine zweite Datei (R-Z2). In der Warnung der Anlage ist "Bitte NICHT erneut speichern ..." der letzte Satz.
 * Die beiden Texte der abgerissenen Antwort (`antwortUnvollstaendigLesen`/`-Schreiben`) stehen bewusst nicht hier: sie
 * raten zum Neuladen und Pruefen, nicht zum Wiederholen.
 */
export const URSACHEN_MIT_WIEDERHOLUNGSRAT: readonly string[] = [
  FEHLERTEXTE.verbindung,
  FEHLERTEXTE.zeitueberschreitung,
  FEHLERTEXTE.serverAntwortetNicht,
  FEHLERTEXTE.serverfehler,
  FEHLERTEXTE.unerwartet,
  FEHLERTEXTE.neuLaden,
];

/**
 * Der Text einer R4-Meldung: `satz` sagt, dass die Aktion gelang und nur das Nachladen scheiterte. Die Ursache steht
 * dahinter, ausser sie ist ein Wiederholungsrat (`URSACHEN_MIT_WIEDERHOLUNGSRAT`): dann steht der Satz allein.
 * Eine Ursache ohne Rat (403, Servertext) bleibt dahinter - sie erklaert, warum das Nachladen scheiterte.
 * `null` (Abbruch, 401) heisst: nichts zu melden; wer den Satz trotzdem zeigen muss (eine Zeile mit Knopf), nimmt
 * ihn selbst.
 */
export function nachladeFehlertext(ursache: unknown, satz: string): string | null {
  const grund = fehlertext(ursache);
  if (grund === null) return null;
  return URSACHEN_MIT_WIEDERHOLUNGSRAT.includes(grund) ? satz : `${satz} ${grund}`;
}

/**
 * `grund` ist das Ergebnis von `fehlertext(fehler)` OHNE Kontext. `null` (Abbruch, 401) ist nicht unklar: dort gibt es
 * nichts zu melden.
 */
export function ausgangUnklar(grund: string | null): boolean {
  return grund !== null && URSACHEN_MIT_UNKLAREM_AUSGANG.includes(grund);
}
